/**
 * Direct messages with one or more people (Slack): who I can add to "New message" (the To: field), the existing
 * conversation for exactly that set of people (the server finds-or-creates by the same rule), the 9-person limit,
 * group DM names ("Ana, Bo, Cy"), "Close conversation" and "Convert to private channel", and the Browse DMs list
 * ("More conversations…"). Pure: no I/O, no clock (callers pass `now`).
 */
import type { Channel } from "@bundle/contracts";
import { capabilitiesFor, isExpired } from "./access.ts";
import { rank } from "./search.ts";
import { channelUnread, type WorkspaceCache } from "./workspace.ts";

/** People in one DM, me included (Slack's limit). */
export const MAX_DM_PEOPLE = 9;

/** A person the To: field offers (the same shape as logic/channels MemberOption). */
export type MemberOption = { userId: string; name: string; avatarUrl?: string; isAgent: boolean; organization?: string };

function isDmKind(kind: string): boolean {
  return kind === "dm" || kind === "group_dm";
}

/** "Ana Souza" → "Ana" (group DM names use first names; a one-word name stays whole). */
export function firstName(displayName: string): string {
  const first = displayName.trim().split(/\s+/)[0];
  return first === undefined || first === "" ? displayName : first;
}

/**
 * My existing conversation with exactly `userIds` (me excluded or not): my self-DM for nobody, the 1:1 DM for one
 * person, the group DM for more. A conversation of another kind with the same people (an imported group DM down to
 * two people) doesn't count, as on the server.
 */
export function conversationWith(cache: WorkspaceCache, userIds: readonly string[]): Channel | undefined {
  const want = [...new Set(userIds.filter((u) => u !== cache.meId))].toSorted();
  const kind = want.length <= 1 ? "dm" : "group_dm";
  for (const id of Object.keys(cache.memberships)) {
    const c = cache.channels[id];
    if (c === undefined || c.kind !== kind || c.dmUserIds === undefined) continue;
    const others = [...new Set(c.dmUserIds.filter((u) => u !== cache.meId))].toSorted();
    if (others.length === want.length && others.every((u, i) => u === want[i])) return c;
  }
  return undefined;
}

/** How many more people the To: field takes (me included in the limit). */
export function recipientsLeft(picked: number): number {
  return Math.max(0, MAX_DM_PEOPLE - 1 - picked);
}

/**
 * People I can add to the To: field, best match first: active members I can see and may message (a guest whose DMs are
 * limited to their sponsor sees only the sponsor; DMs turned off: nobody). Agents too. Empty once the limit is hit.
 */
export function dmCandidates(cache: WorkspaceCache, query: string, picked: readonly string[], now: number, limit: number): MemberOption[] {
  const me = cache.members[cache.meId];
  if (me === undefined || recipientsLeft(picked.length) === 0) return [];
  const can = capabilitiesFor(me, now);
  if (can.dm === "none") return [];
  const sponsor = me.access?.sponsorId;
  const byId: Record<string, MemberOption> = {};
  const candidates: { id: string; text: string; alt?: string }[] = [];
  for (const id of Object.keys(cache.members)) {
    const m = cache.members[id]!;
    if (m.deactivated || id === cache.meId || picked.includes(id) || isExpired(m, now)) continue;
    if (can.dm === "sponsor_only" && id !== sponsor) continue;
    const o: MemberOption = { userId: id, name: m.displayName, isAgent: m.kind === "agent" };
    if (m.avatarUrl !== undefined) o.avatarUrl = m.avatarUrl;
    const org = m.access?.organization?.name;
    if (org !== undefined) o.organization = org;
    byId[id] = o;
    candidates.push(m.fullName !== m.displayName ? { id, text: m.displayName, alt: m.fullName } : { id, text: m.displayName });
  }
  return rank(query, candidates, limit).map((id) => byId[id]!);
}

/** I closed this DM and nothing was posted since ("Close conversation"). */
export function isClosed(cache: WorkspaceCache, channelId: string): boolean {
  const c = cache.channels[channelId];
  const closedAt = cache.memberships[channelId]?.closedAt;
  if (c === undefined || closedAt === undefined || !isDmKind(c.kind)) return false;
  return (c.lastMessageAt ?? 0) <= closedAt;
}

/** "Close conversation" is offered: a DM or group DM I'm in. */
export function canCloseConversation(cache: WorkspaceCache, channelId: string): boolean {
  const c = cache.channels[channelId];
  return c !== undefined && isDmKind(c.kind) && cache.memberships[channelId] !== undefined;
}

/** "Convert to private channel" is offered: a group DM I'm in, and I may create channels. */
export function canConvertToChannel(cache: WorkspaceCache, channelId: string, now: number): boolean {
  const c = cache.channels[channelId];
  const me = cache.members[cache.meId];
  if (c === undefined || me === undefined || c.kind !== "group_dm" || c.archived || cache.memberships[channelId] === undefined) return false;
  return capabilitiesFor(me, now).createChannels;
}

/** "Add people" to a DM: the people of the new, larger conversation (Slack starts a new group DM). */
export function addPeopleTo(cache: WorkspaceCache, channelId: string, add: readonly string[]): string[] {
  const c = cache.channels[channelId];
  const now = (c?.dmUserIds ?? []).filter((u) => u !== cache.meId);
  return [...new Set([...now, ...add.filter((u) => u !== cache.meId)])];
}

function cmpText(a: string, b: string): number {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  return x < y ? -1 : x > y ? 1 : a < b ? -1 : a > b ? 1 : 0;
}

/** One row of Browse DMs. */
export type DmListRow = { channelId: string; name: string; userIds: string[]; unread: boolean; mentions: number; lastMessageAt?: number };

/**
 * Browse DMs ("More conversations…"): all my DMs and group DMs, closed ones too, matching `query` against the
 * participants' names (display and full), most recent first. Names as in the sidebar (logic/sidebar dmName, which
 * this mirrors so the modules don't import each other).
 */
export function browseDms(cache: WorkspaceCache, query: string): DmListRow[] {
  const q = query.trim().toLowerCase();
  const rows: DmListRow[] = [];
  for (const id of Object.keys(cache.memberships)) {
    const c = cache.channels[id];
    if (c === undefined || !isDmKind(c.kind) || c.archived) continue;
    const others = (c.dmUserIds ?? []).filter((u) => u !== cache.meId);
    const name = conversationName(cache, others);
    if (q !== "") {
      const texts = [name, ...others.map((u) => cache.members[u]?.fullName ?? "")].map((t) => t.toLowerCase());
      if (!texts.some((t) => t.includes(q))) continue;
    }
    const u = channelUnread(cache, id);
    const row: DmListRow = { channelId: id, name, userIds: others, unread: u.unread, mentions: u.mentions };
    if (c.lastMessageAt !== undefined) row.lastMessageAt = c.lastMessageAt;
    rows.push(row);
  }
  return rows.toSorted((a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0) || cmpText(a.name, b.name) || cmpText(a.channelId, b.channelId));
}

/** logic/sidebar dmName for these other participants: the person, first names for a group, my name for my self-DM. */
function conversationName(cache: WorkspaceCache, others: readonly string[]): string {
  const nameOf = (id: string) => cache.members[id]?.displayName ?? id;
  if (others.length === 0) return cache.members[cache.meId]?.displayName ?? "";
  if (others.length === 1) return nameOf(others[0]!);
  return others.map((id) => firstName(nameOf(id))).join(", ");
}
